'use client';
/**
 * Pulsing dot that sits next to symbol references across the app.
 *
 * Three workflow states (matches the app-wide red/yellow/green vocab):
 *   - Unread       → severity color (red=high, amber=medium, slate=low)
 *   - Seen         → green (user clicked the dot, viewed the drawer)
 *   - All dismissed/handled → hidden
 *
 * "Seen" is tracked client-side in localStorage as a `{symbol: timestamp}` map.
 * A new alert arriving later than the seen timestamp flips the dot back to
 * its severity color automatically.
 *
 * Hidden when `alertsEnabled` is false.
 *
 * Used by HoldingsList, AlphaZoneHoldingsTable, the research-desk grids,
 * the leaderboard, and as a small label-mate on the 3D scatter ticker
 * labels.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ActionAlertsResponse, AlertSeverity } from '@/lib/action-types';
import { useAlerts } from './AlertsContext';
import { useResource } from '@/lib/use-resource';

interface Props {
  /** Symbol the dot is attached to. */
  symbol: string;
  /** Optional override: dot size in pixels. Default 8. */
  size?: number;
  /** Optional: omit click handler (purely decorative dot, e.g. on 3D
   *  scatter labels where the parent already handles clicks). */
  readonly?: boolean;
}

const SEVERITY_RANK: Record<AlertSeverity, number> = { high: 3, medium: 2, low: 1 };

const SEVERITY_HEX: Record<AlertSeverity, { bg: string; ring: string; pulse: boolean }> = {
  high: { bg: '#ef4444', ring: '#fca5a5', pulse: true },
  medium: { bg: '#f59e0b', ring: '#fcd34d', pulse: false },
  low: { bg: '#64748b', ring: '#94a3b8', pulse: false },
};

const SEEN_HEX = { bg: '#10b981', ring: '#6ee7b7', pulse: false };
const SEEN_KEY = 'signal-dot-seen';

type SeenMap = Record<string, number>;

function loadSeen(): SeenMap {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(window.localStorage.getItem(SEEN_KEY) || '{}'); }
  catch { return {}; }
}

function saveSeen(map: SeenMap) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(SEEN_KEY, JSON.stringify(map)); } catch {}
}

export function SignalDot({ symbol, size = 8, readonly = false }: Props) {
  const { alertsEnabled, openDrawer } = useAlerts();
  const { data } = useResource<ActionAlertsResponse>('/api/action-alerts', 60_000);
  const [seenMap, setSeenMap] = useState<SeenMap>({});

  // Load seen map once on mount; deferred to client so SSR is safe.
  // Same pattern useResource uses to bridge external storage → React state.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSeenMap(loadSeen());
  }, []);

  const { topSeverity, latestTs } = useMemo(() => {
    if (!alertsEnabled) return { topSeverity: null as AlertSeverity | null, latestTs: 0 };
    const alerts = data?.active ?? [];
    let top: AlertSeverity | null = null;
    let topRank = 0;
    let latest = 0;
    for (const a of alerts) {
      if (a.symbol !== symbol) continue;
      if (a.dismissedAt || a.handledAt) continue;
      const rank = SEVERITY_RANK[a.severity];
      if (rank > topRank) { topRank = rank; top = a.severity; }
      const ts = a.ts ? Date.parse(a.ts) : 0;
      if (Number.isFinite(ts) && ts > latest) latest = ts;
    }
    return { topSeverity: top, latestTs: latest };
  }, [symbol, data, alertsEnabled]);

  const onActivate = useCallback(() => {
    const next = { ...seenMap, [symbol]: Date.now() };
    setSeenMap(next);
    saveSeen(next);
    openDrawer(symbol);
  }, [seenMap, symbol, openDrawer]);

  if (!topSeverity) return null;

  const seenTs = seenMap[symbol] ?? 0;
  const isSeen = latestTs > 0 && seenTs >= latestTs;
  const tone = isSeen ? SEEN_HEX : SEVERITY_HEX[topSeverity];
  const px = `${size}px`;
  const ringPx = `${size + 4}px`;

  const dot = (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: ringPx, height: ringPx }}
      title={`${symbol} — ${isSeen ? 'alert read' : `${topSeverity} priority alert`}. Click to open.`}
    >
      {tone.pulse && (
        <span
          className="absolute inline-flex animate-ping opacity-60 rounded-full"
          style={{ width: px, height: px, backgroundColor: tone.ring }}
        />
      )}
      <span
        className="relative rounded-full"
        style={{ width: px, height: px, backgroundColor: tone.bg, boxShadow: `0 0 0 2px ${tone.ring}40` }}
      />
    </span>
  );

  if (readonly) return dot;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onActivate(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onActivate(); } }}
      className="inline-flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/40 rounded-full"
      aria-label={isSeen ? `Open already-read alert for ${symbol}` : `Open ${topSeverity} alert for ${symbol}`}
    >
      {dot}
    </div>
  );
}
